import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useExpenseData, { EXPENSE_CATEGORIES } from "layouts/tables/data/expenseTable";

function Expenses() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedExpense,
    setSelectedExpense,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddExpense,
  } = useExpenseData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading expenses...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Church Expenses"
        subtitle="Record and manage church expenditure."
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddExpense}>
              New Expense
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search expenses..."
        />
      </Card>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedExpense?.id ? "Edit Expense" : "Add Expense"}
        onSave={handleEditSubmit}
        saveLabel={selectedExpense?.id ? "Save Changes" : "Create Expense"}
        maxWidth="xs"
      >
        <FormSection>
          <TextField
            label="Title"
            value={selectedExpense?.title || ""}
            onChange={(e) => setSelectedExpense({ ...selectedExpense, title: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            select
            label="Category"
            value={selectedExpense?.category || "other"}
            onChange={(e) => setSelectedExpense({ ...selectedExpense, category: e.target.value })}
            fullWidth
            margin="normal"
            required
          >
            {EXPENSE_CATEGORIES.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>

        <FormSection>
          <TextField
            label="Amount (GHS)"
            type="number"
            value={selectedExpense?.amount || ""}
            onChange={(e) => setSelectedExpense({ ...selectedExpense, amount: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Expense Date"
            type="date"
            value={selectedExpense?.expenseDate || ""}
            onChange={(e) =>
              setSelectedExpense({ ...selectedExpense, expenseDate: e.target.value })
            }
            fullWidth
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
        </FormSection>

        <FormSection>
          <TextField
            label="Payee"
            value={selectedExpense?.payee || ""}
            onChange={(e) => setSelectedExpense({ ...selectedExpense, payee: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Notes"
            value={selectedExpense?.notes || ""}
            onChange={(e) => setSelectedExpense({ ...selectedExpense, notes: e.target.value })}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedExpense?.title}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Expenses;
